import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatAED } from "@/data/products";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, detailed, setQty, removeItem, subtotal, setCheckoutOpen } = useCart();
  const { t, isAr } = useLanguage();
  const slideX = isAr ? "-100%" : "100%";

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[70] bg-black/40"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: slideX }}
            animate={{ x: 0 }}
            exit={{ x: slideX }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-line bg-white rtl:right-auto rtl:left-0 rtl:border-l-0 rtl:border-r"
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <h3 className="text-lg font-bold text-brandink">{t("cart_title")}</h3>
              <button
                data-testid="cart-close-button"
                onClick={() => setCartOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-brandink transition-colors hover:bg-page"
                aria-label="Close cart"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {detailed.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="cart-empty-state">
                  <ShoppingCart className="h-10 w-10 text-muted" />
                  <p className="text-sm font-semibold text-brandink">{t("cart_empty")}</p>
                  <p className="max-w-[240px] text-xs text-muted">{t("cart_empty_sub")}</p>
                </div>
              ) : (
                <ul className="divide-y divide-line">
                  {detailed.map((item) => (
                    <li key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-3 py-4">
                      <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border border-line bg-white p-1">
                        <img src={item.image} alt={item.name} className="max-h-full max-w-full object-contain" />
                      </span>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium leading-snug text-brandink">{item.name}</p>
                          <button
                            data-testid={`cart-remove-${item.id}`}
                            onClick={() => removeItem(item.id)}
                            className="text-muted transition-colors hover:text-destructive"
                            aria-label={`Remove ${item.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="mt-0.5 text-sm font-bold text-brandink">{formatAED(item.price)}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            data-testid={`cart-qty-minus-${item.id}`}
                            onClick={() => setQty(item.id, item.qty - 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-brandink transition-colors hover:bg-page"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span data-testid={`cart-qty-${item.id}`} className="w-6 text-center text-sm font-semibold">{item.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${item.id}`}
                            onClick={() => setQty(item.id, item.qty + 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-brandink transition-colors hover:bg-page"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {detailed.length > 0 && (
              <div className="border-t border-line px-6 py-5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm text-muted">{t("cart_subtotal")}</span>
                  <span data-testid="cart-subtotal" className="text-xl font-extrabold text-brandink">
                    {formatAED(subtotal)}
                  </span>
                </div>
                <p className="mb-4 text-[11px] font-medium text-success">{t("cart_free")}</p>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="w-full rounded-md bg-brand py-3.5 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark"
                >
                  {t("cart_checkout")}
                </button>
                <p className="mt-3 text-center text-[11px] text-muted">{t("cart_note")}</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
